import { EditableAccountPatch, DraftGroup } from "../draftState";
import { findGroupInScope } from "../draftGroupOperations";
import { NestedEditorRow } from "./nestedRowTypes";

// Removing Save also removed the gates Save enforced, so the two that protected
// data move onto the gesture: the server would happily persist an empty name,
// and a duplicate group name would 4xx and remount the editor over a typo.

export const normalizeAccountPatch = (
    patch: EditableAccountPatch,
): EditableAccountPatch => {
    const normalized: EditableAccountPatch = {};
    if (patch.name !== undefined) {
        normalized.name = patch.name.trim();
    }
    if (patch.codeText !== undefined) {
        const trimmed = patch.codeText?.trim() ?? "";
        normalized.codeText = trimmed.length > 0 ? trimmed : null;
    }
    return normalized;
};

// Two constituents collapse onto one line only while both codes are null, so
// fanning one non-null code onto both would hit the per-business unique index.
const hasCollapsedConstituents = (row: NestedEditorRow): boolean => {
    const entityIds = new Set<number>();
    return row.accounts.some((account) => {
        if (entityIds.has(account.entityId)) {
            return true;
        }
        entityIds.add(account.entityId);
        return false;
    });
};

export const isAccountPatchRefused = ({
    row,
    patch,
}: {
    row: NestedEditorRow;
    patch: EditableAccountPatch;
}): boolean => {
    if (patch.name !== undefined && patch.name.length === 0) {
        return true;
    }
    return patch.codeText != null && hasCollapsedConstituents(row);
};

export const isGroupRenameRefused = ({
    row,
    name,
    groups,
}: {
    row: NestedEditorRow;
    name: string;
    groups: DraftGroup[];
}): boolean => {
    if (name.length === 0) {
        return true;
    }
    const ownIds = new Set(row.groups.map((group) => group.id));
    return row.groups.some((group) => {
        const existing = findGroupInScope({
            groups,
            entityId: group.entityId,
            type: group.type,
            parentGroupId: group.parentGroupId,
            name,
        });
        return existing !== undefined && !ownIds.has(existing.id);
    });
};
